import { inject } from '@angular/core';
import { Router, CanActivateFn, ActivatedRouteSnapshot } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { PermissionService, PermissionKey, UserRole } from '../services/permission.service';

/**
 * Generic Permission Guard Factory
 * Creates a guard that checks for a specific permission
 *
 * Usage in routes:
 * {
 *   path: 'inventory',
 *   canActivate: [permissionGuard('canViewInventory')],
 *   component: InventoryComponent
 * }
 */
export function permissionGuard(permission: PermissionKey): CanActivateFn {
  return async (route, state) => {
    const authService = inject(AuthService);
    const permissionService = inject(PermissionService);
    const router = inject(Router);

    await authService.waitForAuth();

    if (!authService.isAuthenticated()) {
      router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
      return false;
    }

    if (permissionService.hasPermission(permission)) {
      return true;
    }

    // User doesn't have permission - redirect appropriately
    console.warn(`Access denied: missing permission '${permission}'`);

    // If user doesn't have dashboard access, redirect to unauthorized page
    // to avoid infinite redirect loop
    if (permission === 'canAccessDashboard' || !permissionService.hasPermission('canAccessDashboard')) {
      router.navigate(['/unauthorized'], {
        queryParams: { reason: 'permission', missing: permission },
      });
    } else {
      router.navigate(['/dashboard'], {
        queryParams: { accessDenied: 'permission' },
      });
    }
    return false;
  };
}

/**
 * Multiple Permissions Guard Factory (ALL required)
 * Creates a guard that checks for multiple permissions (all must be present)
 *
 * Usage:
 * canActivate: [allPermissionsGuard(['canViewInventory', 'canEditInventory'])]
 */
export function allPermissionsGuard(permissions: PermissionKey[]): CanActivateFn {
  return async (route, state) => {
    const authService = inject(AuthService);
    const permissionService = inject(PermissionService);
    const router = inject(Router);

    await authService.waitForAuth();

    if (!authService.isAuthenticated()) {
      router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
      return false;
    }

    if (permissionService.hasAllPermissions(permissions)) {
      return true;
    }

    console.warn(`Access denied: missing one or more permissions: ${permissions.join(', ')}`);
    router.navigate(['/dashboard'], {
      queryParams: { accessDenied: 'permission' },
    });
    return false;
  };
}

/**
 * Multiple Permissions Guard Factory (ANY required)
 * Creates a guard that checks for multiple permissions (at least one must be present)
 *
 * Usage:
 * canActivate: [anyPermissionGuard(['canViewReports', 'canViewFinancials'])]
 */
export function anyPermissionGuard(permissions: PermissionKey[]): CanActivateFn {
  return async (route, state) => {
    const authService = inject(AuthService);
    const permissionService = inject(PermissionService);
    const router = inject(Router);

    await authService.waitForAuth();

    if (!authService.isAuthenticated()) {
      router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
      return false;
    }

    if (permissionService.hasAnyPermission(permissions)) {
      return true;
    }

    console.warn(`Access denied: requires at least one of: ${permissions.join(', ')}`);
    router.navigate(['/dashboard'], {
      queryParams: { accessDenied: 'permission' },
    });
    return false;
  };
}

/**
 * Role Guard Factory
 * Creates a guard that checks for a specific role
 *
 * Usage:
 * canActivate: [roleGuard('admin')]
 */
export function roleGuard(role: UserRole): CanActivateFn {
  return async (route, state) => {
    const authService = inject(AuthService);
    const permissionService = inject(PermissionService);
    const router = inject(Router);

    await authService.waitForAuth();

    if (!authService.isAuthenticated()) {
      router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
      return false;
    }

    if (permissionService.hasRole(role)) {
      return true;
    }

    console.warn(`Access denied: requires role '${role}'`);
    router.navigate(['/dashboard'], {
      queryParams: { accessDenied: 'role' },
    });
    return false;
  };
}

/**
 * Minimum Role Guard Factory
 * Creates a guard that checks for a minimum role in the hierarchy
 * Role hierarchy: employee < cashier < manager < admin < owner
 *
 * Usage:
 * canActivate: [minimumRoleGuard('manager')] // Allows manager, admin, owner
 */
export function minimumRoleGuard(minimumRole: UserRole): CanActivateFn {
  return async (route, state) => {
    const authService = inject(AuthService);
    const permissionService = inject(PermissionService);
    const router = inject(Router);

    await authService.waitForAuth();

    if (!authService.isAuthenticated()) {
      router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
      return false;
    }

    if (permissionService.hasMinimumRole(minimumRole)) {
      return true;
    }

    console.warn(`Access denied: requires minimum role '${minimumRole}'`);
    router.navigate(['/dashboard'], {
      queryParams: { accessDenied: 'role' },
    });
    return false;
  };
}

// ============================================================================
// PRE-CONFIGURED GUARDS FOR COMMON USE CASES
// ============================================================================

/**
 * Owner Only Guard
 * Only allows business owners
 */
export const ownerGuard: CanActivateFn = async (route, state) => {
  const authService = inject(AuthService);
  const permissionService = inject(PermissionService);
  const router = inject(Router);

  await authService.waitForAuth();

  if (!authService.isAuthenticated()) {
    router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
    return false;
  }

  if (permissionService.isOwner()) {
    return true;
  }

  console.warn('Access denied: owner only');
  router.navigate(['/dashboard'], { queryParams: { accessDenied: 'owner' } });
  return false;
};

/**
 * Admin Guard
 * Allows admin and owner roles
 */
export const adminGuard: CanActivateFn = async (route, state) => {
  const authService = inject(AuthService);
  const permissionService = inject(PermissionService);
  const router = inject(Router);

  await authService.waitForAuth();

  if (!authService.isAuthenticated()) {
    router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
    return false;
  }

  if (permissionService.isAdmin()) {
    return true;
  }

  console.warn('Access denied: admin or owner required');
  router.navigate(['/dashboard'], { queryParams: { accessDenied: 'admin' } });
  return false;
};

/**
 * Manager Guard
 * Allows manager, admin, and owner roles
 */
export const managerGuard: CanActivateFn = async (route, state) => {
  const authService = inject(AuthService);
  const permissionService = inject(PermissionService);
  const router = inject(Router);

  await authService.waitForAuth();

  if (!authService.isAuthenticated()) {
    router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
    return false;
  }

  if (permissionService.isManager()) {
    return true;
  }

  console.warn('Access denied: manager or higher required');
  router.navigate(['/dashboard'], { queryParams: { accessDenied: 'manager' } });
  return false;
};

/**
 * Settings Guard
 * Only allows users who can edit settings
 */
export const settingsGuard: CanActivateFn = async (route, state) => {
  const authService = inject(AuthService);
  const permissionService = inject(PermissionService);
  const router = inject(Router);

  await authService.waitForAuth();

  if (!authService.isAuthenticated()) {
    router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
    return false;
  }

  if (permissionService.canEditBusinessSettings()) {
    return true;
  }

  console.warn('Access denied: settings permission required');
  router.navigate(['/dashboard'], { queryParams: { accessDenied: 'settings' } });
  return false;
};

/**
 * Financial Reports Guard
 * Only allows users who can view financial data
 */
export const financialGuard: CanActivateFn = async (route, state) => {
  const authService = inject(AuthService);
  const permissionService = inject(PermissionService);
  const router = inject(Router);

  await authService.waitForAuth();

  if (!authService.isAuthenticated()) {
    router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
    return false;
  }

  if (permissionService.canViewFinancialReports()) {
    return true;
  }

  console.warn('Access denied: financial access required');
  router.navigate(['/dashboard'], { queryParams: { accessDenied: 'financial' } });
  return false;
};
